"use client";

import Image from "next/image";
import { cn } from "@/lib/utils";
import type { ImageDataType } from "@/shared/types";

interface ImageGridGalleryProps {
  images: ImageDataType[];
  columnsPerRow: 2 | 3 | 4;
  onImageClick: (index: number) => void;
  gridShellClass?: string;
  gridCellClass?: string;
  className?: string;
}

/** Same cell padding as CardsBlock. */
const gridCellPaddingClass =
  "px-4 py-10 md:px-6 md:pt-10 md:pb-12 desktop:px-10 desktop:py-20";

const TABLET_COLUMNS = 2;

function getGridImageAspectClass(columnsPerRow: 2 | 3 | 4): string {
  if (columnsPerRow === 2) {
    return "aspect-[640/400]";
  }

  if (columnsPerRow === 4) {
    return "aspect-[280/200]";
  }

  return "aspect-[400/280]";
}

function getRowFillerCount(itemsCount: number, columns: number): number {
  if (columns <= 0) {
    return 0;
  }

  const remainder = itemsCount % columns;
  if (remainder === 0) {
    return 0;
  }

  return columns - remainder;
}

export function ImageGridGallery({
  images,
  columnsPerRow,
  onImageClick,
  gridShellClass,
  gridCellClass,
  className,
}: ImageGridGalleryProps) {
  if (images.length === 0) {
    return null;
  }

  const useSectionGrid = Boolean(gridShellClass && gridCellClass);
  const imageAspectClass = getGridImageAspectClass(columnsPerRow);
  const mobileFillers = getRowFillerCount(images.length, TABLET_COLUMNS);
  const tabletFillers = getRowFillerCount(images.length, TABLET_COLUMNS);
  const desktopFillers = getRowFillerCount(images.length, columnsPerRow);

  return (
    <div
      className={cn(
        useSectionGrid
          ? gridShellClass
          : "grid w-full grid-cols-2 gap-4 md:gap-6 desktop:grid-cols-3",
        !useSectionGrid && columnsPerRow === 2 && "desktop:grid-cols-2",
        !useSectionGrid && columnsPerRow === 4 && "desktop:grid-cols-4",
        className
      )}
    >
      {images.map((image, index) => {
        const imageButton = (
          <button
            type="button"
            onClick={() => onImageClick(index)}
            className={cn(
              "group relative w-full cursor-pointer overflow-hidden bg-muted",
              imageAspectClass
            )}
            aria-label={`Открыть изображение ${index + 1}`}
          >
            <Image
              src={image.src}
              alt={image.alt || ""}
              title={image.title ?? undefined}
              fill
              sizes={
                columnsPerRow >= 4
                  ? "(max-width: 767px) 50vw, (max-width: 1439px) 33vw, 25vw"
                  : columnsPerRow === 3
                    ? "(max-width: 767px) 50vw, (max-width: 1439px) 33vw, 33vw"
                    : "(max-width: 767px) 50vw, 50vw"
              }
              className="object-cover transition-opacity duration-200 group-hover:opacity-90"
              priority={index < 4}
            />
          </button>
        );

        if (useSectionGrid) {
          return (
            <div key={index} className={gridCellClass}>
              <div className={gridCellPaddingClass}>{imageButton}</div>
            </div>
          );
        }

        return <div key={index}>{imageButton}</div>;
      })}

      {useSectionGrid && mobileFillers > 0 &&
        Array.from({ length: mobileFillers }).map((_, index) => (
          <div
            key={`grid-filler-mobile-${index}`}
            className={cn(gridCellClass, "md:hidden")}
            aria-hidden
          />
        ))}

      {useSectionGrid && tabletFillers > 0 &&
        Array.from({ length: tabletFillers }).map((_, index) => (
          <div
            key={`grid-filler-tablet-${index}`}
            className={cn(gridCellClass, "hidden md:block desktop:hidden")}
            aria-hidden
          />
        ))}

      {useSectionGrid && desktopFillers > 0 &&
        Array.from({ length: desktopFillers }).map((_, index) => (
          <div
            key={`grid-filler-desktop-${index}`}
            className={cn(gridCellClass, "hidden desktop:block")}
            aria-hidden
          />
        ))}
    </div>
  );
}
